/**
 * "Back two chords." — the command bar answering, and then getting out of the way.
 *
 * Deliberately not the same object as `SaveToast`. A save is a thing that now exists and
 * can be renamed or opened, so it gets a pill with actions in the middle of the screen.
 * A command reply is the app repeating what it understood; it has no actions, it is worth
 * about a second and a half of attention, and it belongs directly above the bar the phrase
 * was typed into so the eye does not have to travel. One quiet line, no border, no buttons.
 *
 * `aria-live="polite"` rather than `assertive`: this is confirmation, and interrupting a
 * screen reader mid-sentence to confirm something the player just did is the audible
 * version of the chatter the whole voice feature is fenced off to avoid.
 */

import { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useCalmMotion } from './motionPreference.js';
import { CALM_FADE } from '../graph/motion.js';
import type { CommandMessage } from '../input/useCommandBar.js';

/** Long enough to read six words. Shorter than the save toast, which offers a choice. */
export const COMMAND_TOAST_MS = 2600;

export interface CommandToastProps {
  message: CommandMessage | null;
  onDismiss?: () => void;
  durationMs?: number;
}

export function CommandToast({
  message,
  onDismiss,
  durationMs = COMMAND_TOAST_MS,
}: CommandToastProps): React.JSX.Element {
  const calm = useCalmMotion();
  const id = message?.id;

  useEffect(() => {
    if (id === undefined || !onDismiss) return;
    const timer = setTimeout(onDismiss, durationMs);
    return () => clearTimeout(timer);
  }, [id, durationMs, onDismiss]);

  return (
    <AnimatePresence initial={false}>
      {message ? (
        <motion.p
          // Keyed on the id so a repeated sentence re-animates instead of sitting still,
          // which is the only signal that the second attempt was heard at all.
          key={message.id}
          className="hc-cmd-toast"
          data-testid="hc-command-toast"
          role="status"
          aria-live="polite"
          initial={calm ? { opacity: 0 } : { opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={CALM_FADE}
        >
          {message.text}
        </motion.p>
      ) : null}
    </AnimatePresence>
  );
}

export default CommandToast;
